import { FACTION_STANDINGS, FACTION_TYPES, type TFactionStanding, type TFactionType } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  Loader,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import clsx from "clsx"
import { useCallback, useMemo, useRef, useState } from "react"
import { z } from "zod"
import type { IFaction, IFactionCreateBody, IFactionEditBody, IWorldRelationship } from "../../../lib/api"
import { VoxStatusPill } from "../../VoxStatusPill"
import { useWorldEntityGrid } from "../../WorldEntityShell/useWorldEntityGrid"
import { WorldEntityGrid } from "../../WorldEntityShell/WorldEntityGrid"
import { WorldEntityToolbar } from "../../WorldEntityShell/WorldEntityToolbar"
import shared from "../../WorldEntityShell/worldEntity.module.css"
import classes from "../styles.module.css"
import type { IViewProps } from "./interfaces"

/**
 * Dumb view for FactionsView. Receives already-fetched factions / NPCs /
 * relationships and surfaces pure rendering + local UI state. Shared grid
 * machinery (filter, selection, counts, pending-first sort) comes from
 * useWorldEntityGrid; the toolbar + card grid from the WorldEntityShell
 * primitives. What stays faction-specific: the standing-coloured left border,
 * the leader/member NPC pickers, and the inline faction↔faction relationship
 * editor with its own draft state.
 *
 * All side effects — API calls, query invalidation, the delete + bulk confirm
 * modals — fire on the smart wrapper at ../index.tsx via the callback props.
 */

interface IFactionFormValues {
  name: string
  aliases: string[]
  type: TFactionType | ""
  partyStanding: TFactionStanding
  leaderNpcId: string | ""
  memberNpcIds: string[]
  description: string
}

const RELATIONSHIP_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "allied_with", label: "allied with" },
  { value: "friendly_with", label: "friendly with" },
  { value: "neutral_with", label: "neutral with" },
  { value: "hostile_to", label: "hostile to" },
  { value: "enemy_of", label: "enemy of" },
  { value: "at_war_with", label: "at war with" },
  { value: "vassal_of", label: "vassal of" },
  { value: "sworn_to", label: "sworn to" },
]

const factionSchema = z.object({
  name: z.string().min(1, "Name is required"),
  aliases: z.array(z.string()),
  type: z.union([z.enum([...FACTION_TYPES] as [TFactionType, ...TFactionType[]]), z.literal("")]),
  partyStanding: z.enum([...FACTION_STANDINGS] as [TFactionStanding, ...TFactionStanding[]]),
  leaderNpcId: z.string(),
  memberNpcIds: z.array(z.string()),
  description: z.string(),
})

const factionIsPending = (faction: IFaction): boolean => faction.pending
const factionName = (faction: IFaction): string => faction.name
const factionId = (faction: IFaction): string => faction.id

function emptyFormValues(): IFactionFormValues {
  return {
    name: "",
    aliases: [],
    type: "",
    partyStanding: "neutral",
    leaderNpcId: "",
    memberNpcIds: [],
    description: "",
  }
}

function formValuesFromFaction(f: IFaction): IFactionFormValues {
  return {
    name: f.name,
    aliases: f.aliases,
    type: f.type ?? "",
    partyStanding: f.partyStanding,
    leaderNpcId: f.leaderNpcId ?? "",
    memberNpcIds: f.memberNpcIds,
    description: f.description ?? "",
  }
}

function buildCreatePayload(v: IFactionFormValues): IFactionCreateBody {
  const out: IFactionCreateBody = { name: v.name.trim(), partyStanding: v.partyStanding }
  if (v.aliases.length) out.aliases = v.aliases
  if (v.type) out.type = v.type
  if (v.leaderNpcId) out.leaderNpcId = v.leaderNpcId
  if (v.memberNpcIds.length) out.memberNpcIds = v.memberNpcIds
  if (v.description.trim()) out.description = v.description.trim()
  return out
}

function buildEditPayload(v: IFactionFormValues): IFactionEditBody {
  return {
    name: v.name.trim(),
    aliases: v.aliases,
    type: v.type ? v.type : null,
    partyStanding: v.partyStanding,
    leaderNpcId: v.leaderNpcId ? v.leaderNpcId : null,
    memberNpcIds: v.memberNpcIds,
    description: v.description.trim() ? v.description.trim() : null,
  }
}

function standingClass(standing: TFactionStanding): string {
  switch (standing) {
    case "allied":
      return classes.standingAllied
    case "friendly":
      return classes.standingFriendly
    case "hostile":
      return classes.standingHostile
    case "enemy":
      return classes.standingEnemy
    case "unknown":
      return classes.standingUnknown
    default:
      return classes.standingNeutral
  }
}

/**
 * Map a faction's party-standing to the design-system VoxStatusPill tone.
 *   allied / friendly → active   (lifelike arcane glow — they fight with you)
 *   hostile / enemy   → tension  (crimson — sharpened blades)
 *   neutral           → revealed (ether — known, but uncommitted)
 *   unknown           → neutral  (no standing established)
 */
function standingTone(standing: TFactionStanding): "active" | "tension" | "revealed" | "neutral" {
  switch (standing) {
    case "allied":
    case "friendly":
      return "active"
    case "hostile":
    case "enemy":
      return "tension"
    case "neutral":
      return "revealed"
    default:
      return "neutral"
  }
}

export const FactionsViewView = ({
  factions,
  npcs,
  relationships,
  isLoading,
  initialSelectedId,
  bulkBusy,
  createPending,
  updatePending,
  acceptPending,
  deletePending,
  createRelationshipPending,
  onCreateSubmit,
  onUpdateSubmit,
  onAcceptFaction,
  onDeleteFaction,
  onAddRelationship,
  onDeleteRelationship,
  onAcceptAllPending,
  onDeleteAllPending,
}: IViewProps): React.JSX.Element => {
  const { i18n } = useLingui()

  const {
    filter,
    setFilter,
    selectedId,
    selected,
    editing,
    setEditing,
    createModalOpen,
    setCreateModalOpen,
    pendingCount,
    acceptedCount,
    visibleItems: visibleFactions,
    selectDetail,
    closeDrawer,
  } = useWorldEntityGrid<IFaction>({
    items: factions,
    isPending: factionIsPending,
    getName: factionName,
    getId: factionId,
    initialEntityId: initialSelectedId,
  })

  const npcNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const n of npcs) m.set(n.id, n.name)
    return m
  }, [npcs])

  const factionNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const f of factions) m.set(f.id, f.name)
    return m
  }, [factions])

  const npcOptions = useMemo(() => npcs.map((n) => ({ value: n.id, label: n.name })), [npcs])

  // Faction-faction edges involving the selected faction. We split into
  // outbound (this faction → other) and inbound (other → this faction) so
  // the drawer can label "this faction is allied_with X" vs "Y is sworn_to
  // this faction" without flipping the relationship_type semantics.
  const factionRelationships = useMemo<IWorldRelationship[]>(
    () => relationships.filter((r) => r.sourceKind === "faction" && r.targetKind === "faction"),
    [relationships],
  )
  const outboundRelationships = useMemo(
    () => (selected ? factionRelationships.filter((r) => r.sourceId === selected.id) : []),
    [factionRelationships, selected],
  )
  const inboundRelationships = useMemo(
    () => (selected ? factionRelationships.filter((r) => r.targetId === selected.id) : []),
    [factionRelationships, selected],
  )

  // Inline-add form state — kept local to the drawer so it resets when the
  // drawer closes / the selected faction changes. We track the previous
  // selectedId via a ref so the reset only fires on real changes (the
  // simpler `useEffect(..., [selectedId])` pattern trips biome's
  // exhaustive-deps because the body doesn't read selectedId).
  const [newRelationshipType, setNewRelationshipType] = useState<string>("allied_with")
  const [newRelationshipTargetId, setNewRelationshipTargetId] = useState<string>("")
  const lastSelectedFactionRef = useRef<string | null>(null)
  if (lastSelectedFactionRef.current !== selectedId) {
    lastSelectedFactionRef.current = selectedId
    if (newRelationshipTargetId !== "") setNewRelationshipTargetId("")
    if (newRelationshipType !== "allied_with") setNewRelationshipType("allied_with")
  }

  const handleAddRelationship = useCallback((): void => {
    if (!selected || !newRelationshipTargetId || !newRelationshipType) return
    onAddRelationship(selected.id, {
      relationshipType: newRelationshipType,
      targetId: newRelationshipTargetId,
    })
    setNewRelationshipTargetId("")
  }, [selected, newRelationshipTargetId, newRelationshipType, onAddRelationship])

  const editForm = useForm<IFactionFormValues>({
    mode: "uncontrolled",
    initialValues: emptyFormValues(),
    validate: zodResolver(factionSchema),
  })

  const createForm = useForm<IFactionFormValues>({
    mode: "uncontrolled",
    initialValues: emptyFormValues(),
    validate: zodResolver(factionSchema),
  })

  const handleEditClick = useCallback((): void => {
    if (!selected) return
    editForm.setValues(formValuesFromFaction(selected))
    setEditing(true)
  }, [editForm, selected, setEditing])

  const handleEditSubmit = useCallback(
    async (values: IFactionFormValues): Promise<void> => {
      if (!selected) return
      try {
        await onUpdateSubmit(selected.id, buildEditPayload(values))
        setEditing(false)
      } catch {
        // Smart wrapper surfaces the error notification; keep edit mode open so the user's input survives.
      }
    },
    [selected, onUpdateSubmit, setEditing],
  )

  const handleCreateSubmit = useCallback(
    async (values: IFactionFormValues): Promise<void> => {
      try {
        await onCreateSubmit(buildCreatePayload(values))
        setCreateModalOpen(false)
      } catch {
        // Smart wrapper surfaces the error notification; keep the modal open so the user's input survives.
      }
    },
    [onCreateSubmit, setCreateModalOpen],
  )

  const handleAccept = useCallback((): void => {
    if (!selected) return
    onAcceptFaction(selected.id)
  }, [selected, onAcceptFaction])

  const handleDelete = useCallback((): void => {
    if (!selected) return
    onDeleteFaction(selected.id)
  }, [selected, onDeleteFaction])

  const handleOpenCreate = useCallback((): void => {
    createForm.setValues(emptyFormValues())
    setCreateModalOpen(true)
  }, [createForm, setCreateModalOpen])

  if (isLoading) {
    return (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    )
  }

  return (
    <div className={shared.root}>
      <WorldEntityToolbar
        title={<Trans>Factions</Trans>}
        acceptedCount={acceptedCount}
        pendingCount={pendingCount}
        filter={filter}
        onFilterChange={setFilter}
        bulkBusy={bulkBusy}
        onAcceptAllPending={onAcceptAllPending}
        onDeleteAllPending={onDeleteAllPending}
        addLabel={<Trans>Add faction</Trans>}
        onAdd={handleOpenCreate}
      />

      <WorldEntityGrid
        items={visibleFactions}
        emptyMessage={
          filter === "pending" ? (
            <Trans>No pending factions to review.</Trans>
          ) : filter === "accepted" ? (
            <Trans>No accepted factions yet.</Trans>
          ) : (
            <Trans>No factions yet. Add one manually.</Trans>
          )
        }
        renderCard={(f) => (
          <Card
            key={f.id}
            withBorder
            padding="md"
            className={clsx(
              shared.card,
              classes.factionCard,
              standingClass(f.partyStanding),
              f.pending && shared.pendingCard,
            )}
            onClick={() => selectDetail(f.id)}
          >
            <div className={shared.cardHeader}>
              <Text className={shared.cardName} lineClamp={1}>
                {f.name}
              </Text>
              {f.type && (
                <Badge size="xs" color="highlight" variant="light">
                  {f.type}
                </Badge>
              )}
            </div>
            {f.leaderNpcId && npcNameById.get(f.leaderNpcId) && (
              <Text fz="sm" c="dimmed">
                <Trans>led by {npcNameById.get(f.leaderNpcId) ?? ""}</Trans>
              </Text>
            )}
            {f.description && (
              <Text fz="sm" c="dimmed" lineClamp={2} mt="xs">
                {f.description}
              </Text>
            )}
            <div className={shared.cardMeta}>
              <VoxStatusPill tone={standingTone(f.partyStanding)}>{f.partyStanding}</VoxStatusPill>
              {f.pending && (
                <Badge size="xs" color="warning" variant="filled">
                  <Trans>pending</Trans>
                </Badge>
              )}
              {f.memberNpcIds.length > 0 && (
                <Badge size="xs" color="brand" variant="outline">
                  {f.memberNpcIds.length} members
                </Badge>
              )}
            </div>
          </Card>
        )}
      />

      <Drawer
        opened={!!selected}
        onClose={closeDrawer}
        position="right"
        size="md"
        title={selected ? selected.name : ""}
        padding="lg"
      >
        {selected && !editing && (
          <Stack gap="md">
            <Group gap="xs">
              {selected.type && (
                <Badge color="highlight" variant="light">
                  {selected.type}
                </Badge>
              )}
              <VoxStatusPill tone={standingTone(selected.partyStanding)}>{selected.partyStanding}</VoxStatusPill>
              {selected.pending && (
                <Badge color="warning" variant="filled">
                  <Trans>pending</Trans>
                </Badge>
              )}
            </Group>

            {selected.leaderNpcId && npcNameById.get(selected.leaderNpcId) && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Leader</Trans>
                </span>
                <Text fz="sm">{npcNameById.get(selected.leaderNpcId)}</Text>
              </div>
            )}

            {selected.memberNpcIds.length > 0 && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Named members</Trans>
                </span>
                <Group gap="xs">
                  {selected.memberNpcIds.map((id) => {
                    const name = npcNameById.get(id)
                    if (!name) return null
                    return (
                      <Badge key={id} variant="outline" color="brand" size="sm">
                        {name}
                      </Badge>
                    )
                  })}
                </Group>
              </div>
            )}

            {selected.aliases.length > 0 && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Aliases</Trans>
                </span>
                <Group gap="xs">
                  {selected.aliases.map((alias) => (
                    <Badge key={alias} variant="outline" color="brand" size="sm">
                      {alias}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}

            {selected.description && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Description</Trans>
                </span>
                <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {selected.description}
                </Text>
              </div>
            )}

            <div className={shared.drawerSection}>
              <span className={shared.drawerSectionLabel}>
                <Trans>Relationships with other factions</Trans>
              </span>
              {outboundRelationships.length === 0 && inboundRelationships.length === 0 && (
                <Text fz="xs" c="dimmed">
                  <Trans>No relationships recorded yet.</Trans>
                </Text>
              )}
              <Stack gap={4}>
                {outboundRelationships.map((r) => {
                  const targetName = factionNameById.get(r.targetId) ?? r.targetId
                  return (
                    <Group key={r.id} gap="xs" align="center" wrap="nowrap">
                      <Badge size="sm" variant="light" color="info">
                        {r.relationshipType.replace(/_/g, " ")}
                      </Badge>
                      <Text fz="sm" style={{ flex: 1 }}>
                        {targetName}
                      </Text>
                      <ActionIcon
                        size="xs"
                        color="danger"
                        variant="subtle"
                        onClick={() => onDeleteRelationship(r.id)}
                        aria-label={i18n._(msg`Remove`)}
                      >
                        ×
                      </ActionIcon>
                    </Group>
                  )
                })}
                {inboundRelationships.map((r) => {
                  const sourceName = factionNameById.get(r.sourceId) ?? r.sourceId
                  return (
                    <Group key={r.id} gap="xs" align="center" wrap="nowrap">
                      <Text fz="sm" style={{ flex: 1 }}>
                        {sourceName}
                      </Text>
                      <Badge size="sm" variant="light" color="highlight">
                        {r.relationshipType.replace(/_/g, " ")}
                      </Badge>
                      <ActionIcon
                        size="xs"
                        color="danger"
                        variant="subtle"
                        onClick={() => onDeleteRelationship(r.id)}
                        aria-label={i18n._(msg`Remove`)}
                      >
                        ×
                      </ActionIcon>
                    </Group>
                  )
                })}
              </Stack>

              <Group gap="xs" mt="xs" align="flex-end">
                <Select
                  size="xs"
                  label={i18n._(msg`Type`)}
                  data={RELATIONSHIP_TYPE_OPTIONS}
                  value={newRelationshipType}
                  onChange={(v) => setNewRelationshipType(v ?? "allied_with")}
                  style={{ width: 140 }}
                />
                <Select
                  size="xs"
                  label={i18n._(msg`Target faction`)}
                  placeholder={i18n._(msg`Pick faction`)}
                  searchable
                  clearable
                  data={factions.filter((f) => f.id !== selected.id).map((f) => ({ value: f.id, label: f.name }))}
                  value={newRelationshipTargetId}
                  onChange={(v) => setNewRelationshipTargetId(v ?? "")}
                  style={{ flex: 1 }}
                />
                <Button
                  size="xs"
                  onClick={handleAddRelationship}
                  loading={createRelationshipPending}
                  disabled={!newRelationshipTargetId}
                >
                  <Trans>Add</Trans>
                </Button>
              </Group>
            </div>

            <div className={shared.drawerActions}>
              <Button variant="default" onClick={handleEditClick}>
                <Trans>Edit</Trans>
              </Button>
              {selected.pending && (
                <Button color="success" onClick={handleAccept} loading={acceptPending}>
                  <Trans>Accept</Trans>
                </Button>
              )}
              <Button color="danger" variant="outline" onClick={handleDelete} loading={deletePending}>
                <Trans>Delete</Trans>
              </Button>
            </div>
          </Stack>
        )}

        {selected && editing && (
          <form onSubmit={editForm.onSubmit(handleEditSubmit)}>
            <Stack gap="md">
              <TextInput label={i18n._(msg`Name`)} key={editForm.key("name")} {...editForm.getInputProps("name")} />
              <TagsInput
                label={i18n._(msg`Aliases`)}
                description={i18n._(msg`Press enter after each alias`)}
                key={editForm.key("aliases")}
                {...editForm.getInputProps("aliases")}
              />
              <Select
                label={i18n._(msg`Type`)}
                clearable
                data={FACTION_TYPES.map((t) => ({ value: t, label: t }))}
                value={editForm.getValues().type}
                onChange={(v) => editForm.setFieldValue("type", (v ?? "") as TFactionType | "")}
              />
              <SegmentedControl
                size="xs"
                fullWidth
                data={FACTION_STANDINGS.map((s) => ({ value: s, label: s }))}
                value={editForm.getValues().partyStanding}
                onChange={(v) => editForm.setFieldValue("partyStanding", v as TFactionStanding)}
              />
              <Select
                label={i18n._(msg`Leader (NPC)`)}
                placeholder={i18n._(msg`(none)`)}
                clearable
                searchable
                data={npcOptions}
                value={editForm.getValues().leaderNpcId}
                onChange={(v) => editForm.setFieldValue("leaderNpcId", v ?? "")}
              />
              <MultiSelect
                label={i18n._(msg`Named members (NPCs)`)}
                searchable
                clearable
                data={npcOptions}
                value={editForm.getValues().memberNpcIds}
                onChange={(v) => editForm.setFieldValue("memberNpcIds", v)}
              />
              <Textarea
                label={i18n._(msg`Description`)}
                autosize
                minRows={3}
                key={editForm.key("description")}
                {...editForm.getInputProps("description")}
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setEditing(false)}>
                  <Trans>Cancel</Trans>
                </Button>
                <Button type="submit" loading={updatePending}>
                  <Trans>Save</Trans>
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Drawer>

      <Modal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title={i18n._(msg`Add faction`)}
        size="lg"
      >
        <form onSubmit={createForm.onSubmit(handleCreateSubmit)}>
          <Stack gap="md">
            <TextInput
              label={i18n._(msg`Name`)}
              required
              key={createForm.key("name")}
              {...createForm.getInputProps("name")}
            />
            <TagsInput
              label={i18n._(msg`Aliases`)}
              description={i18n._(msg`Press enter after each alias`)}
              key={createForm.key("aliases")}
              {...createForm.getInputProps("aliases")}
            />
            <Select
              label={i18n._(msg`Type`)}
              clearable
              data={FACTION_TYPES.map((t) => ({ value: t, label: t }))}
              value={createForm.getValues().type}
              onChange={(v) => createForm.setFieldValue("type", (v ?? "") as TFactionType | "")}
            />
            <SegmentedControl
              size="xs"
              fullWidth
              data={FACTION_STANDINGS.map((s) => ({ value: s, label: s }))}
              value={createForm.getValues().partyStanding}
              onChange={(v) => createForm.setFieldValue("partyStanding", v as TFactionStanding)}
            />
            <Select
              label={i18n._(msg`Leader (NPC)`)}
              placeholder={i18n._(msg`(none)`)}
              clearable
              searchable
              data={npcOptions}
              value={createForm.getValues().leaderNpcId}
              onChange={(v) => createForm.setFieldValue("leaderNpcId", v ?? "")}
            />
            <MultiSelect
              label={i18n._(msg`Named members (NPCs)`)}
              searchable
              clearable
              data={npcOptions}
              value={createForm.getValues().memberNpcIds}
              onChange={(v) => createForm.setFieldValue("memberNpcIds", v)}
            />
            <Textarea
              label={i18n._(msg`Description`)}
              autosize
              minRows={3}
              key={createForm.key("description")}
              {...createForm.getInputProps("description")}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreateModalOpen(false)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" loading={createPending}>
                <Trans>Create</Trans>
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  )
}
